"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { resendOTP, verifyOTP } from "../redux/slices/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../context/AuthContext";

const OTP_TIME = 30; // 10 minutes

function VerifyOTP() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setAuthenticatedUser } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [error, setError] = useState("");
  const [isResending, setIsResending] = useState(false);

  // Timer states
  const [timeLeft, setTimeLeft] = useState(OTP_TIME);
  const [canResend, setCanResend] = useState(false);

  const authError = useSelector((state) => state.auth.error);

  // Get email from URL
  const searchParams = new URLSearchParams(window.location.search);
  const emailFromParams = searchParams.get("email");

  /* ---------------- TIMER LOGIC ---------------- */
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }

    const interval = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec < 10 ? "0" : ""}${sec}`;
  };

  /* ---------------- RESEND OTP ---------------- */
  const handleResend = async () => {
    if (!canResend) return;

    setIsResending(true);
    try {
      await dispatch(resendOTP({ email: emailFromParams })).unwrap();

      // Reset timer
      setTimeLeft(OTP_TIME);
      setCanResend(false);
    } catch (error) {
      console.log("Failed to resend OTP:", error);
    } finally {
      setIsResending(false);
    }
  };

  /* ---------------- VERIFY OTP ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const result = await dispatch(
        verifyOTP({
          email: emailFromParams,
          emailOTP: otpCode,
          phoneOTP: phoneCode,
        })
      );

      if (verifyOTP.fulfilled.match(result)) {
        setAuthenticatedUser(result.payload.data.user);
        window.location.href = "/session-check";
      } else {
        setError("Verification failed. Please try again.");
      }
    } catch (err) {
      console.error("Verification error:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="auth-card">
      <div className="auth-header">
        <h2 className="auth-title">Verify Your Identity</h2>
        <p className="auth-description">
          Check your email inbox and cell phone for your One Time Codes.
        </p>
      </div>

      <div className="auth-form">
        <form onSubmit={handleSubmit}>
          {authError && <div className="error-alert">{authError}</div>}
          {error && <div className="error-alert">{error}</div>}

          <div className="form-group mb-2">
            <label>Email Code</label>
            <input
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              type="text"
              placeholder="Enter email code"
              required
              className="form-input form-control w-100"
            />
          </div>

          <div className="form-group mb-2">
            <label>Phone Code</label>
            <input
              value={phoneCode}
              onChange={(e) => setPhoneCode(e.target.value)}
              type="text"
              placeholder="Enter phone code"
              required
              className="form-input form-control w-100"
            />
          </div>

          {/* -------- TIMER / RESEND -------- */}
          <div className="text-end text-14 my-4">
            {!canResend ? (
              <span className="text-muted">
                Resend available in{" "}
                <strong>{formatTime(timeLeft)}</strong>
              </span>
            ) : (
              <>
              Did't receive the verfication? {" "}    
              <span
                className="link-button"
                onClick={handleResend}
                style={{
                  cursor: isResending ? "not-allowed" : "pointer",
                  opacity: isResending ? 0.6 : 1,
                }}
              >
                {isResending ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" />
                    Resending...
                  </>
                ) : (
                  "Resend Code"
                )}
              </span>
              </>
            )}
          </div>

          <button
            type="submit"
            className="auth-button w-full"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="d-flex align-items-center justify-content-center">
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Verifying...
              </div>
            ) : (
              "Verify"
            )}
          </button>
        </form>

        <div className="auth-links">
          <div className="auth-switch">
            <button
              type="button"
              className="link-button"
              onClick={() => navigate("/login")}
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyOTP;
